import React, { useCallback, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Navigation } from 'swiper/modules';
import { Swiper as ISwiper, SwiperOptions } from 'swiper/types';
import { Swiper as ReactSwiper, SwiperSlide } from 'swiper/react';
import ChevronLeft from '~/icons/chevron-left.svg';
import ChevronRight from '~/icons/chevron.svg';
import styles from './PLPCarousel.module.scss';
import { PLPCarouselItem, PLPCarouselItemProps } from './PLPCarouselItem';
import useSwiperRef from '~/shared/components/Carousel/hooks/useSwiperRef';
import { SkipLink } from '~/templates/pages/components/DynamicLayout/components/Standard/styled';
import { useRouter } from 'next/router';

interface PLPCarouselProps extends React.HTMLAttributes<HTMLDivElement> {
    className?: string;
    items: PLPCarouselItemProps[];
    isPillVariant?: boolean;
}

export const PLPCarousel: React.FC<PLPCarouselProps> = ({
    items,
    className,
    isPillVariant,
    ...rest
}) => {
    const prevRef = useRef<HTMLButtonElement>(null);
    const nextRef = useRef<HTMLButtonElement>(null);
    const prev = useSwiperRef({ ref: prevRef });
    const next = useSwiperRef({ ref: nextRef });
    const router = useRouter();
    const [shouldHidePrev, setShouldHidePrev] = useState(true);
    const [shouldHideNext, setShouldHideNext] = useState(false);
    const [isLocked, setIsLocked] = useState(false);

    const options = useMemo<SwiperOptions>(
        () => ({
            slidesPerView: 'auto',
            freeMode: true,
            mousewheel: true,
            modules: [Navigation],
            navigation: { prevEl: prev.element, nextEl: next.element },
        }),
        [prev.element, next.element],
    );

    const validateNavigation = useCallback((swiper: ISwiper) => {
        setShouldHidePrev(swiper.isBeginning);
        setShouldHideNext(swiper.isEnd);
    }, []);

    const onSliderLock = useCallback(() => setIsLocked(true), []);

    const onSliderUnlock = useCallback(
        (swiper: ISwiper) => {
            setIsLocked(false);
            validateNavigation(swiper);
        },
        [validateNavigation],
    );

    const onReachBeginning = useCallback(() => setShouldHidePrev(true), []);
    const onReachEnd = useCallback(() => setShouldHideNext(true), []);

    return (
        <div className={styles.carousel} {...rest} aria-label="Subcategories carousel">
            <SkipLink
                href="#plpFacets"
                onClick={(e) => {
                    e.preventDefault();
                    router.replace(e.currentTarget.href);
                    e.currentTarget.blur();
                }}
            >
                Spring over
            </SkipLink>
            <ReactSwiper
                className={clsx(styles.swiper, className)}
                {...options}
                onReachBeginning={onReachBeginning}
                onReachEnd={onReachEnd}
                onSlideChange={validateNavigation}
                onUnlock={onSliderUnlock}
                onLock={onSliderLock}
            >
                {items.map((item, index) => (
                    <SwiperSlide className={styles.slide} key={`item-${index}`}>
                        <PLPCarouselItem {...item} isPillVariant={isPillVariant} />
                    </SwiperSlide>
                ))}
            </ReactSwiper>

            <button
                ref={prevRef}
                className={clsx(styles.navigation, styles.previous, {
                    [styles.isHidden]: shouldHidePrev,
                    [styles.noNavigation]: isLocked,
                })}
                onClick={(e) => e.currentTarget.blur()}
            >
                <ChevronLeft className={styles.navigationIcon} />
            </button>

            <button
                ref={nextRef}
                className={clsx(styles.navigation, styles.next, {
                    [styles.isHidden]: shouldHideNext,
                    [styles.noNavigation]: isLocked,
                })}
                onClick={(e) => e.currentTarget.blur()}
            >
                <ChevronRight className={styles.navigationIcon} />
            </button>

            <div
                className={clsx(styles.fade, styles.fadeLeft, {
                    [styles.isHidden]: shouldHidePrev || isLocked,
                })}
                aria-hidden
            />
            <div
                className={clsx(styles.fade, styles.fadeRight, {
                    [styles.isHidden]: shouldHideNext || isLocked,
                })}
                aria-hidden
            />
        </div>
    );
};
